import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, Circle, ChevronLeft, PlayCircle, Sparkles, Pencil } from "lucide-react";
import api from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { Avatar } from "@/components/common";
import { Stars, StarInput } from "@/components/Stars";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [active, setActive] = useState(0);
  const [buying, setBuying] = useState(false);
  const positioned = useRef(false);

  const load = async () => {
    const res = await api.get(`/courses/${courseId}`);
    setCourse(res.data);
    if (!positioned.current && res.data.unlocked) {
      const idx = (res.data.lessons || []).findIndex((l) => !l.completed);
      setActive(idx >= 0 ? idx : 0);
      positioned.current = true;
    }
  };

  useEffect(() => { load(); }, [courseId]);

  const buy = async () => {
    setBuying(true);
    try {
      const res = await api.post("/payments/checkout", {
        course_id: courseId, origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBuying(false);
    }
  };

  const complete = async (lessonId) => {
    try {
      await api.post(`/courses/${courseId}/lessons/${lessonId}/complete`);
      toast.success("Lesson complete · +15 pts");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  if (!course) return <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>;

  const lesson = course.lessons[active];
  const locked = !course.unlocked;
  const isOwner = user && course.author_id && user.user_id === course.author_id;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/courses")} data-testid="course-back-button"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ChevronLeft size={16} /> All courses
      </button>

      <div className="border border-border bg-card rounded-xl">
        <div className="relative aspect-[21/7] overflow-hidden border-b border-border">
          {course.cover_image && <img src={course.cover_image} alt="" className="w-full h-full object-cover warm-img" />}
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 sm:p-8">
            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${
              course.is_paid ? "bg-gold text-accent-foreground rounded-md" : "bg-background/90 border border-border"}`}>
              {course.is_paid ? `Executive · $${course.price}` : "Free"}
            </span>
            <h1 className="font-serif font-bold text-3xl sm:text-4xl mt-3 tracking-tight text-white drop-shadow">{course.title}</h1>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          {course.categories?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {course.categories.map((c) => (
                <span key={c} className="text-xs text-cobalt bg-secondary rounded-full px-2.5 py-0.5">{c}</span>
              ))}
            </div>
          )}
          {course.author && (
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => navigate(`/members/${course.author.user_id}`)}
                className="flex items-center gap-2 group" data-testid="course-author-link">
                <Avatar src={course.author.picture} name={course.author.name} size={32} />
                <span className="text-sm">by <span className="font-semibold group-hover:text-cobalt transition-colors">{course.author.name}</span></span>
              </button>
              {isOwner && (
                <button onClick={() => navigate(`/courses/${course.course_id}/edit`)} data-testid="edit-course-button"
                  className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-sm hover:bg-secondary/60 transition-colors">
                  <Pencil size={13} /> Edit
                </button>
              )}
            </div>
          )}
          {isOwner && course.status && course.status !== "published" && (
            <div className={`mb-4 rounded-lg border p-3 text-sm ${course.status === "pending" ? "border-amber/40 bg-amber/10 text-amber" : "border-destructive/40 bg-destructive/10 text-destructive"}`}
              data-testid="course-status-banner">
              {course.status === "pending" ? "This course is in review and not yet visible to others." : "This course was declined and is hidden from others. Edit and resubmit."}
            </div>
          )}
          <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">{course.description}</p>
          <div className="mt-5 flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <div className="flex justify-between text-xs font-mono text-muted-foreground mb-1">
                <span>{course.completed_count}/{course.lesson_count} lessons</span>
                <span>{course.progress_pct}%</span>
              </div>
              <div className="h-[3px] bg-border"><div className="h-full bg-gold" style={{ width: `${course.progress_pct}%` }} /></div>
            </div>
          </div>
        </div>
      </div>

      {locked ? (
        <div className="border border-gold/50 bg-card p-8 text-center" data-testid="course-paywall">
          <Lock size={28} className="mx-auto text-gold" />
          <h2 className="font-serif text-2xl mt-4">Unlock the Executive Track</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
            Purchase once and keep lifetime access to all {course.lesson_count} lessons in this course.
          </p>
          <ul className="mt-6 space-y-2 text-sm max-w-xs mx-auto text-left">
            {course.lessons.map((l) => (
              <li key={l.lesson_id} className="flex items-center gap-2 text-muted-foreground">
                <Sparkles size={13} className="text-gold shrink-0" /> {l.title}
              </li>
            ))}
          </ul>
          <button onClick={buy} disabled={buying} data-testid="stripe-checkout-pay-button"
            className="mt-8 inline-flex items-center gap-2 bg-gold text-accent-foreground rounded-md px-8 py-3.5 text-sm font-medium hover:bg-gold-hover transition-colors disabled:opacity-50">
            {buying ? "Redirecting…" : `Buy for $${course.price}`}
          </button>
          <p className="mt-3 text-xs text-muted-foreground">Secure checkout via Stripe · test card 4242 4242 4242 4242</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-4 border border-border bg-card rounded-xl divide-y divide-border">
            <div className="px-4 py-3 eyebrow text-muted-foreground">Lessons</div>
            {course.lessons.map((l, i) => (
              <button key={l.lesson_id} onClick={() => setActive(i)}
                data-testid={`lesson-item-${l.lesson_id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                  i === active ? "bg-secondary/60 text-foreground" : "text-muted-foreground hover:bg-secondary/40"}`}>
                {l.completed ? <CheckCircle2 size={16} className="text-gold shrink-0" /> : <Circle size={16} className="shrink-0" />}
                <span className="flex-1">{l.title}</span>
              </button>
            ))}
          </div>

          <div className="lg:col-span-8 border border-border bg-card rounded-xl p-6 sm:p-8" data-testid="lesson-viewer">
            <div className="flex items-center gap-2 eyebrow text-gold mb-3">
              <PlayCircle size={13} /> Lesson {active + 1}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">{lesson.title}</h2>
            {lesson.video_url && (
              <a href={lesson.video_url} target="_blank" rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-gold hover:underline">
                <PlayCircle size={15} /> Watch video
              </a>
            )}
            <div className="mt-5 gold-rule" />
            <div className="mt-5 text-sm sm:text-[15px] leading-[1.8] text-foreground/90">
              <Markdown>{lesson.content}</Markdown>
            </div>
            <button onClick={() => complete(lesson.lesson_id)} disabled={lesson.completed}
              data-testid={`complete-lesson-${lesson.lesson_id}`}
              className="mt-8 inline-flex items-center gap-2 border border-gold text-gold px-5 py-2.5 text-sm hover:bg-gold hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gold">
              <CheckCircle2 size={15} /> {lesson.completed ? "Completed" : "Mark complete"}
            </button>
          </div>
        </div>
      )}

      <Reviews course={course} isOwner={isOwner} />
    </div>
  );
}

function Reviews({ course, isOwner }) {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/courses/${course.course_id}/reviews`).then((r) => setReviews(r.data));
  useEffect(() => { load(); }, [course.course_id]);

  const submit = async () => {
    if (rating < 1) return toast.error("Pick a star rating");
    setBusy(true);
    try {
      await api.post(`/courses/${course.course_id}/reviews`, { rating, comment });
      toast.success("Thanks for your review!");
      setComment("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card rounded-xl p-6 sm:p-8" data-testid="reviews-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif text-2xl font-bold">Reviews</h2>
        <div className="flex items-center gap-2">
          <Stars value={course.rating_avg} size={16} />
          <span className="text-sm text-muted-foreground font-mono">
            {course.rating_count > 0 ? `${course.rating_avg} · ${course.rating_count} review${course.rating_count > 1 ? "s" : ""}` : "No reviews yet"}
          </span>
        </div>
      </div>

      {!isOwner && (
        <div className="mt-5 border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Your rating:</span>
            <StarInput value={rating} onChange={setRating} />
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
            placeholder="Share what you thought (optional)…" data-testid="review-comment-input"
            className="w-full mt-3 bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none resize-none focus:border-cobalt transition-colors" />
          <button onClick={submit} disabled={busy} data-testid="submit-review-button"
            className="mt-3 bg-cobalt text-white rounded-md px-5 py-2 text-sm font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50">
            {busy ? "Submitting…" : "Submit review"}
          </button>
        </div>
      )}

      <div className="mt-6 space-y-5">
        {reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">Be the first to review this course.</p>
        ) : reviews.map((r) => (
          <div key={r.review_id} className="flex gap-3" data-testid={`review-${r.review_id}`}>
            <Avatar src={r.author.picture} name={r.author.name} size={36} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold">{r.author.name}</span>
                <Stars value={r.rating} size={12} />
              </div>
              {r.comment && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{r.comment}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
